// Página de ruta para los códigos del Grupo 4 (Módulo H) — los 30 códigos
// que construyen/editan su propio recurso FHIR discreto en vez de un
// QuestionnaireResponse (ver discretos/index.ts). Mismo patrón de gate de
// especialidad + subject/encounter memoizados que MotorFormularioPage.tsx/
// Formulario001Page.tsx.
//
// A diferencia de Formulario001Page (que edita UN Encounter, con un
// `documentoId` real y estable), cada formulario discreto puede crear
// VARIOS recursos distintos por guardado (ej. 020 crea un Observation por
// signo vital) — no hay un único id de recurso que represente "el
// documento". El componente hijo reporta los pares pregunta/respuesta que
// acaba de guardar vía `onGuardado` (en vez de `onSubmitted`), y esta página
// arma el PDF (modo `pares`, punto 11 del pedido) + PanelFirmaImpresion con
// un `documentoId` sintético por guardado — mismo criterio que ya usa
// RecetaWizard para sus propios documentoId sintéticos (`receta/<token>`).
import type { Patient, Reference } from '@medplum/fhirtypes';
import { useMedplum } from '@medplum/react-hooks';
import type { JSX } from 'react';
import { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useOutletContext, useParams, useSearchParams } from 'react-router';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { Button } from '@/components/ui/button';
import { InsigniaFormularioPropio } from '@/components/InsigniaFormularioPropio';
import { codigosFormularioDeEspecialidad } from '@/lib/catalogoEspecialidades';
import { useEncounterValidado } from '@/lib/useEncounterValidado';
import { useEspecialidadDelPracticante } from '@/lib/useEspecialidadDelPracticante';
import { TRAMO_B } from '@/lib/tramoB';
import type { PatientChartOutletContext } from '@/paciente/PatientChartPage';
import { FORMULARIOS_DISCRETOS, type Par } from './discretos';
import { PanelFirmaImpresion } from './PanelFirmaImpresion';
import { CODIGOS_CON_CEDULA_PROFESIONAL, fuenteFormularioPdf } from '@logiacare/hce-pdf';
import type { DatosCurvasCrecimientoPdf, DatosDenverPdf, DatosEncabezadoMsp, DatosGraficoConstantesUciPdf, DatosOdontogramaPdf, DatosPartogramaPdf, DatosSignosVitalesPdf, DatosSiluetaPdf } from '@logiacare/hce-pdf';

export interface FormularioDiscretoPageProps {
  /**
   * El código del formulario. Las rutas de este grupo se registran una por
   * código con un path ESTÁTICO (`formulario/010A`, no `formulario/:codigo`)
   * — ver routes.tsx — así que no viaja como param de URL (`useParams()`
   * no tiene nada que capturar en un segmento fijo); se lo pasa el `<Route
   * element>` directamente. Bug real encontrado en verificación en vivo
   * 2026-09-01: antes se leía de `useParams().codigo`, que siempre daba
   * `undefined` acá y bloqueaba los 30 códigos del Grupo 4 para cualquier
   * especialidad.
   */
  readonly codigo: string;
  /**
   * Aviso de "se guardó algo" para los llamantes que además muestran el
   * historial de lo guardado en la misma pantalla (hoy NotaTurnoTab.tsx, la
   * pestaña "Nota de turno" de Enfermería). Las rutas normales de
   * `formulario/<código>` no lo pasan y se comportan igual que siempre.
   */
  readonly onGuardado?: () => void;
}

export function FormularioDiscretoPage({ codigo, onGuardado }: FormularioDiscretoPageProps): JSX.Element {
  const { t } = useTranslation();
  const medplum = useMedplum();
  const { id } = useParams() as { id: string };
  const [searchParams] = useSearchParams();
  const encounterParam = searchParams.get('encounter');
  const especialidad = useEspecialidadDelPracticante();

  const subject = useMemo<Reference<Patient>>(() => ({ reference: `Patient/${id}` }), [id]);
  const encounterValidado = useEncounterValidado(subject, encounterParam);

  const [paresGuardados, setParesGuardados] = useState<Par[]>([]);
  const [datosEncabezadoMsp, setDatosEncabezadoMsp] = useState<DatosEncabezadoMsp>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo manda la nota SOAPIE. */
  const [relacionado, setRelacionado] = useState<Reference>();
  /** Recurso que se crea al firmar (nota SOAPIE): ver `crearRelacionado` en PanelFirmaImpresion.tsx. */
  const [crearRelacionado, setCrearRelacionado] = useState<() => Promise<Reference>>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo manda 033 (odontograma), para dibujarlo en el PDF firmado. */
  const [diagramaOdontograma, setDiagramaOdontograma] = useState<DatosOdontogramaPdf>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo manda 028 (Denver II), para dibujarlo en el PDF firmado. */
  const [diagramaDenver, setDiagramaDenver] = useState<DatosDenverPdf>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo mandan 018A/019 (Fase 1B), para dibujarlo en el PDF firmado. */
  const [diagramaSignosVitales, setDiagramaSignosVitales] = useState<DatosSignosVitalesPdf>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo manda 051 (Fase 4), para dibujarlo en el PDF firmado. */
  const [diagramaPartograma, setDiagramaPartograma] = useState<DatosPartogramaPdf>();
  /** Ver `onGuardado` en discretos/index.ts — hoy lo mandan 113 (1 elemento) y 114 (2 elementos), para dibujarlos en el PDF firmado. */
  const [diagramasGraficoConstantesUci, setDiagramasGraficoConstantesUci] = useState<readonly DatosGraficoConstantesUciPdf[]>();
  /** Ver `onGuardado` en discretos/index.ts — hoy solo lo manda 003 (Fase 7), para dibujar la silueta corporal en el PDF firmado. */
  const [siluetaCorporal, setSiluetaCorporal] = useState<DatosSiluetaPdf>();
  const [curvasCrecimiento, setCurvasCrecimiento] = useState<DatosCurvasCrecimientoPdf>();
  const [numGuardados, setNumGuardados] = useState(0);
  // Descartar (pedido explícito de Sue 2026-09-27): SOLO existe antes de
  // guardar (numGuardados === 0) — una vez guardado, la única salida es
  // firmar (ver `pendienteFirma`/bloqueo de salida más abajo).
  const [descartado, setDescartado] = useState(false);
  const [firmado, setFirmado] = useState(false);
  // `reportarPendienteFirma` (no `outlet` completo) como dependencia: es el
  // setState estable de PatientChartPage, así que este efecto NO se
  // reejecuta en cada render del padre (que arma un objeto de contexto
  // nuevo en cada render) — solo cuando de verdad cambia `pendienteFirma`.
  const reportarPendienteFirma = useOutletContext<PatientChartOutletContext | undefined>()?.reportarPendienteFirma;
  const pendienteFirma = numGuardados > 0 && !firmado && !descartado;
  useEffect(() => {
    reportarPendienteFirma?.(pendienteFirma);
  }, [reportarPendienteFirma, pendienteFirma]);
  useEffect(() => () => reportarPendienteFirma?.(false), [reportarPendienteFirma]);

  if (especialidad.cargando || encounterValidado.cargando) {
    return <p className="text-sm text-hce-muted">{t('formulario.cargando')}</p>;
  }
  // farmacia-dispensacion no es un formulario del Anexo 1 — es un módulo
  // interno opcional por tenant (a cargo del Director Técnico Q.F./B.F., no
  // de una especialidad médica), gateado aparte por
  // useFarmaciaInternaHabilitada (ver FarmaciaTab.tsx). No aparece en
  // codigosFormularioDeEspecialidad de NINGUNA especialidad — aplicarle este
  // gate lo bloquearía siempre, para cualquier practicante.
  //
  // `!especialidad.codigo` (sin especialidad resuelta) también bloquea —
  // ANTES no bloqueaba ("cuenta legacy sin PractitionerRole, no restringir")
  // pero eso saltaba el gate por completo para CUALQUIER código. Bug real
  // confirmado en logs de producción 2026-09-03 (Caddy access.log): una
  // sesión real con login exitoso pero sin ninguna búsqueda de
  // PractitionerRole pudo abrir 010A/010B/012A/012B/013A/013B/118 sin
  // ninguna restricción de especialidad. Investigado antes de aplicar esto:
  // no hay ningún cliente real hoy sin PractitionerRole (solo cuentas de
  // prueba sueltas y las cuentas demo de facilitaclinic/multiples-sedes, a
  // las que se les asignó especialidad de una vez, 2026-09-03).
  if (codigo !== 'farmacia-dispensacion' && (!especialidad.codigo || !codigosFormularioDeEspecialidad(especialidad.codigo).includes(codigo))) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.fueraDeEspecialidadTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.fueraDeEspecialidadDetalle')}</AlertDescription>
      </Alert>
    );
  }

  const Componente = FORMULARIOS_DISCRETOS[codigo];
  if (!Componente) {
    return (
      <Alert variant="red">
        <AlertTitle>{t('formulario.noEncontradoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.noEncontradoDetalle', { codigo })}</AlertDescription>
      </Alert>
    );
  }

  const titulo = t(`hub.formulario.${codigo}`);
  // Fase 4 de "receta digital", pedido explícito del usuario: el botón
  // "Envío digital" también en las pestañas Documentos/Laboratorio y
  // Estudios, para todo formulario que se firme ahí — DocumentosTab.tsx usa
  // `soloGrupo="documentos"` y LabsTab.tsx `soloGrupo="diagnostico"` (ver
  // TRAMO_B), así que basta con mirar a qué grupo pertenece el código.
  const grupo = TRAMO_B.find((e) => e.codigo === codigo)?.grupo;
  const envioDigitalHabilitado = grupo === 'documentos' || grupo === 'diagnostico';

  // Identificación del profesional en el cuerpo del PDF: los formularios cuyo papel pide "número de documento de identificación"
  // (`CODIGOS_CON_CEDULA_PROFESIONAL`, misma lista que usa el servidor) piden la cédula en el panel de firma; el resto imprime la
  // matrícula del servidor. Solo aplica al modo `msp` (el servidor ignora la cédula en los demás modos).
  const encabezadoFirma: DatosEncabezadoMsp | undefined =
    datosEncabezadoMsp && CODIGOS_CON_CEDULA_PROFESIONAL.includes(codigo) ? { ...datosEncabezadoMsp, etiquetaIdentificacion: t('formulario.firma.cedulaEtiqueta') } : datosEncabezadoMsp;

  if (descartado) {
    // Descartar (pedido explícito de Sue 2026-09-27): solo existe ANTES de
    // guardar — con numGuardados === 0 no hay nada persistido todavía.
    return (
      <Alert>
        <AlertTitle>{t('formulario.descartadoTitulo')}</AlertTitle>
        <AlertDescription>{t('formulario.descartadoDetalle')}</AlertDescription>
      </Alert>
    );
  }

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex flex-wrap items-center gap-2">
          <h3 className="text-base font-medium text-hce-ink">{titulo}</h3>
          <InsigniaFormularioPropio codigo={codigo} />
        </div>
        {numGuardados === 0 && (
          <Button type="button" variant="outline" size="sm" onClick={() => setDescartado(true)}>
            {t('formulario.descartar')}
          </Button>
        )}
      </div>
      {encounterValidado.invalido && (
        <Alert variant="orange">
          <AlertTitle>{t('formulario.encounterInvalidoTitulo')}</AlertTitle>
          <AlertDescription>{t('formulario.encounterInvalidoDetalle')}</AlertDescription>
        </Alert>
      )}
      <Componente
        subject={subject}
        encounter={encounterValidado.encounter}
        onGuardado={(pares, encabezadoMsp, recursoRelacionado, diagrama, diagramaDenverNuevo, diagramaSignosVitalesNuevo, diagramaPartogramaNuevo, diagramasGraficoConstantesUciNuevo, siluetaCorporalNueva, curvasCrecimientoNuevas) => {
          setParesGuardados(pares);
          setDatosEncabezadoMsp(encabezadoMsp);
          const diferido = typeof recursoRelacionado === 'function';
          setRelacionado(diferido ? undefined : recursoRelacionado);
          setCrearRelacionado(diferido ? () => recursoRelacionado : undefined);
          setDiagramaOdontograma(diagrama);
          setDiagramaDenver(diagramaDenverNuevo);
          setDiagramaSignosVitales(diagramaSignosVitalesNuevo);
          setDiagramaPartograma(diagramaPartogramaNuevo);
          setDiagramasGraficoConstantesUci(diagramasGraficoConstantesUciNuevo);
          setSiluetaCorporal(siluetaCorporalNueva);
          setCurvasCrecimiento(curvasCrecimientoNuevas);
          setNumGuardados((n) => n + 1);
          // Con recurso diferido nada se guardó todavía: el aviso al llamante va al firmar (`onFirmado`).
          if (!diferido) onGuardado?.();
        }}
      />
      {numGuardados > 0 && (
        <>
          <Alert>
            <AlertTitle>{crearRelacionado ? t('formulario.pendienteFirmaTitulo') : t('formulario.guardadoTitulo')}</AlertTitle>
            <AlertDescription>{crearRelacionado ? t('formulario.pendienteFirmaDetalle') : t('formulario.guardadoDetalle')}</AlertDescription>
          </Alert>
          <PanelFirmaImpresion
            key={numGuardados}
            documentoId={`${subject.reference}/formulario-${codigo}-${numGuardados}`}
            tipoDocumento={codigo}
            titulo={titulo}
            subject={subject}
            etiquetaIdentificacion={encabezadoFirma?.etiquetaIdentificacion}
            envioDigitalHabilitado={envioDigitalHabilitado}
            relacionado={relacionado}
            crearRelacionado={crearRelacionado}
            onFirmado={async () => {
              setFirmado(true);
              if (crearRelacionado) onGuardado?.();
            }}
            especificacionBuilder={async () => {
              if (encabezadoFirma) {
                return {
                  modo: 'msp',
                  pares: paresGuardados,
                  datosEncabezadoMsp: encabezadoFirma,
                  diagramaOdontograma,
                  diagramaDenver,
                  diagramaSignosVitales,
                  diagramaPartograma,
                  diagramasGraficoConstantesUci,
                  siluetaCorporal,
                  curvasCrecimiento,
                };
              }
              const paciente = await medplum.readReference(subject);
              return {
                modo: 'pares',
                titulo,
                pares: paresGuardados,
                paciente: {
                  nombre: paciente.name?.[0] ? `${paciente.name[0].given?.join(' ') ?? ''} ${paciente.name[0].family ?? ''}`.trim() : (subject.reference ?? ''),
                  identificador: paciente.identifier?.[0]?.value,
                },
                // 028A-1/028B/028C (sin encabezado MSP): también llevan sus curvas al PDF firmado.
                curvasCrecimiento,
                fuenteFormulario: fuenteFormularioPdf(codigo),
              };
            }}
          />
        </>
      )}
    </div>
  );
}
